import React, { useEffect, useMemo, useState } from "react";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import "./mcImportEngine.css";
import "./mcConfig.css";
import { canAccessAdminTabs, useCurrentMcProfile } from "../utils/profileAccess";
import {
  loadSections,
  recoverSectionsFromOperationalHistory,
  normalizeSectionId,
  saveSections,
  MC_SECTION_EVENT,
  loadCycles,
  saveCycles,
  normalizeCycleId,
  nextCycleName,
  MC_CYCLE_EVENT,
} from "../utils/mcSections";

function blankSection() {
  return { name: "", description: "", active: true };
}

function blankCycle() {
  return { name: "", description: "", active: true };
}

export default function MorpheusCountSections() {
  const currentProfile = useCurrentMcProfile();
  const canManage = canAccessAdminTabs(currentProfile);
  const [sections, setSections] = useState(() => loadSections());
  const [cycles, setCycles] = useState(() => loadCycles());
  const [form, setForm] = useState(blankSection());
  const [cycleForm, setCycleForm] = useState(() => blankCycle());
  const [message, setMessage] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCycleTarget, setDeleteCycleTarget] = useState(null);

  useEffect(() => {
    const sync = () => {
      let nextSections = loadSections();
      if (!nextSections.length) nextSections = recoverSectionsFromOperationalHistory();
      const nextCycles = loadCycles();
      setSections(nextSections);
      setCycles(nextCycles);
      setCycleForm((current) => current?.name || current?.description ? current : blankCycle());
    };
    sync();
    window.addEventListener(MC_SECTION_EVENT, sync);
    window.addEventListener(MC_CYCLE_EVENT, sync);
    window.addEventListener("storage", sync);
    window.addEventListener("mc-cloud-sync", sync);
    return () => {
      window.removeEventListener(MC_SECTION_EVENT, sync);
      window.removeEventListener(MC_CYCLE_EVENT, sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("mc-cloud-sync", sync);
    };
  }, []);

  const stats = useMemo(() => ({
    total: sections.length,
    active: sections.filter((section) => section.active !== false).length,
    inactive: sections.filter((section) => section.active === false).length,
    cycles: cycles.length,
  }), [sections, cycles]);

  function persist(nextSections, note = "Sections saved.") {
    const saved = saveSections(nextSections);
    setSections(saved);
    setMessage(note);
  }


  function persistCycles(nextCycles, note = "Cycles saved.") {
    const saved = saveCycles(nextCycles);
    setCycles(saved);
    setCycleForm(blankCycle());
    setMessage(note);
  }

  function addCycle(event) {
    event.preventDefault();
    const name = String(cycleForm.name || "").trim();
    if (!name) return setMessage("Enter a cycle name first.");

    const normalizedName = normalizeCycleId(name);
    const duplicateName = cycles.some((cycle) => {
      const currentName = String(cycle?.name || "").trim();
      return currentName && normalizeCycleId(currentName) === normalizedName;
    });
    if (duplicateName) return setMessage("That cycle already exists.");

    // Cycle IDs are stable because existing sessions can reference them. If a cycle was
    // renamed, its old ID must not block reusing the old display name for a new cycle.
    const usedIds = new Set(cycles.map((cycle) => String(cycle?.cycleId || cycle?.id || "").trim()).filter(Boolean));
    let cycleId = normalizedName;
    let suffix = 2;
    while (usedIds.has(cycleId)) {
      cycleId = `${normalizedName}-${suffix}`;
      suffix += 1;
    }

    persistCycles([
      ...cycles,
      {
        cycleId,
        id: cycleId,
        name,
        description: String(cycleForm.description || ""),
        active: cycleForm.active !== false,
        createdAt: new Date().toISOString(),
        sortOrder: cycles.length,
      },
    ], "Cycle created.");
  }

  function moveCycle(cycleId, direction) {
    const currentIndex = cycles.findIndex((cycle) => (cycle.cycleId || cycle.id) === cycleId);
    if (currentIndex < 0) return;
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= cycles.length) return;

    const reordered = [...cycles];
    [reordered[currentIndex], reordered[nextIndex]] = [reordered[nextIndex], reordered[currentIndex]];
    persistCycles(
      reordered.map((cycle, index) => ({ ...cycle, sortOrder: index })),
      "Cycle order updated."
    );
  }

  function updateCycle(cycleId, patch) {
    const next = cycles.map((cycle) => {
      if ((cycle.cycleId || cycle.id) !== cycleId) return cycle;
      const name = patch.name !== undefined ? patch.name : cycle.name;
      return {
        ...cycle,
        ...patch,
        cycleId: cycle.cycleId || normalizeCycleId(name),
        id: cycle.id || cycle.cycleId || normalizeCycleId(name),
        name,
        updatedAt: new Date().toISOString(),
      };
    });
    persistCycles(next, "Cycle updated.");
  }

  function confirmRemoveCycle() {
    if (!deleteCycleTarget?.cycleId && !deleteCycleTarget?.id) return;
    const targetId = deleteCycleTarget.cycleId || deleteCycleTarget.id;
    persistCycles(cycles.filter((cycle) => (cycle.cycleId || cycle.id) !== targetId), `${deleteCycleTarget.name || "Cycle"} removed.`);
    setDeleteCycleTarget(null);
  }

  function addSection(event) {
    event.preventDefault();
    const name = String(form.name || "").trim();
    if (!name) return setMessage("Enter a section name first.");
    const sectionId = normalizeSectionId(name);
    if (sections.some((section) => normalizeSectionId(section.sectionId || section.id || section.name) === sectionId)) {
      return setMessage("That section already exists.");
    }
    persist([
      ...sections,
      {
        sectionId,
        id: sectionId,
        name,
        description: String(form.description || "").trim(),
        active: form.active !== false,
        createdAt: new Date().toISOString(),
      },
    ], "Section created.");
    setForm(blankSection());
  }

  function updateSection(sectionId, patch) {
    const next = sections.map((section) => {
      if ((section.sectionId || section.id) !== sectionId) return section;
      const name = patch.name !== undefined ? patch.name : section.name;
      return {
        ...section,
        ...patch,
        sectionId: section.sectionId || normalizeSectionId(name),
        id: section.id || section.sectionId || normalizeSectionId(name),
        name,
        updatedAt: new Date().toISOString(),
      };
    });
    persist(next, "Section updated.");
  }

  function confirmRemoveSection() {
    if (!deleteTarget?.sectionId && !deleteTarget?.id) return;
    const targetId = deleteTarget.sectionId || deleteTarget.id;
    persist(sections.filter((section) => (section.sectionId || section.id) !== targetId), `${deleteTarget.name || "Section"} removed.`);
    setDeleteTarget(null);
  }

  if (!canManage) {
    return (
      <div className="mc-enginePage mc-sectionsPage mc-tabPage">
        <div className="mc-enginePage__grid" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

        <McPageHeader activeKey="sections" subtitle="Only admin profiles can manage ownership sections." />
        <McPageFooter />
      </div>
    );
  }

  return (
    <div className="mc-enginePage mc-sectionsPage mc-tabPage">
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      <McPageHeader activeKey="sections" />

      <section className="mc-enginePage__heroCards mc-sectionsStats">
        <article className="mc-engineCard"><span>Total Sections</span><strong>{stats.total}</strong></article>
        <article className="mc-engineCard"><span>Active</span><strong>{stats.active}</strong></article>
        <article className="mc-engineCard"><span>Inactive</span><strong>{stats.inactive}</strong></article>
        <article className="mc-engineCard"><span>Cycles</span><strong>{stats.cycles}</strong></article>
      </section>

      <section className="mc-enginePage__content">
        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Create section</h3>
              <p>Sections are the ownership foundation. Later, categories and profiles will be assigned to these sections.</p>
            </div>
            <div className="mc-engineBadge">Ownership Setup</div>
          </div>
          <form className="mc-fieldGrid" onSubmit={addSection}>
            <div className="mc-fieldControl">
              <label>Section Name</label>
              <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Example: Bakery" />
            </div>
            <div className="mc-fieldControl">
              <label>Description</label>
              <input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Example: Bakery production inventory" />
            </div>
            <div className="mc-inlineActions" style={{ marginTop: 14 }}>
              <button className="mc-engineBtn mc-engineBtn--primary" type="submit">Create Section</button>
            </div>
          </form>
          {message ? <div className="mc-helperText" style={{ marginTop: 12 }}>{message}</div> : null}
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Live sections</h3>
              <p>Manage the ownership areas that will later control category ownership, profile access, and section counts.</p>
            </div>
            <div className="mc-engineBadge">{sections.length} Sections</div>
          </div>
          {sections.length ? (
            <div className="mc-liveRuleTable">
              <div className="mc-liveRuleTable__head" style={{ gridTemplateColumns: "1fr 1.35fr .5fr .42fr" }}>
                <div>Section</div><div>Description</div><div className="mc-liveRuleTable__headAction">Status</div><div className="mc-liveRuleTable__headAction">Remove</div>
              </div>
              {sections.map((section) => {
                const sectionId = section.sectionId || section.id;
                return (
                  <div className="mc-liveRuleTable__row" style={{ gridTemplateColumns: "1fr 1.35fr .5fr .42fr" }} key={sectionId}>
                    <div><input value={section.name || ""} onChange={(event) => updateSection(sectionId, { name: event.target.value })} /></div>
                    <div><input value={section.description || ""} onChange={(event) => updateSection(sectionId, { description: event.target.value })} placeholder="Optional" /></div>
                    <div className="mc-liveRuleAction">
                      <button type="button" className={`mc-configToggle ${section.active !== false ? "is-on" : ""}`} onClick={() => updateSection(sectionId, { active: section.active === false })}>
                        <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span><strong>{section.active !== false ? "Active" : "Inactive"}</strong>
                      </button>
                    </div>
                    <div className="mc-liveRuleAction">
                      <button
                        type="button"
                        className="mc-engineBtn mc-siteRemovePill"
                        onClick={() => setDeleteTarget(section)}
                        style={{ borderColor: "rgba(255, 83, 117, 0.45)", color: "#ff8fa3" }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mc-emptyState"><strong>No sections created yet.</strong><p>Create Bakery, Kitchen, Front Counter, Barista, Retail, or any ownership area used inside your site.</p></div>
          )}
        </article>
      </section>


      <section className="mc-enginePage__content" style={{ marginTop: 22 }}>
        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Count cycles</h3>
              <p>Create named count windows for the day, such as Cycle 1, Morning, Late Shift, or Closing.</p>
            </div>
            <div className="mc-engineBadge">{cycles.length} Cycles</div>
          </div>
          <form className="mc-fieldGrid" onSubmit={addCycle}>
            <div className="mc-fieldControl">
              <label>Cycle Name</label>
              <input value={cycleForm.name} onChange={(event) => setCycleForm({ ...cycleForm, name: event.target.value })} placeholder="Example: Morning Count" />
            </div>
            <div className="mc-fieldControl">
              <label>Description</label>
              <input value={cycleForm.description} onKeyDownCapture={(event) => event.stopPropagation()} onKeyUpCapture={(event) => event.stopPropagation()} onChange={(event) => setCycleForm({ ...cycleForm, description: event.target.value })} placeholder="Example: Between 8-9am must be completed" />
            </div>
            <div className="mc-inlineActions" style={{ marginTop: 14 }}>
              <button className="mc-engineBtn mc-engineBtn--primary" type="submit">Create Cycle</button>
            </div>
          </form>
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Live cycles</h3>
              <p>Cycles will be selected during Imports and stored against each section or full count session.</p>
            </div>
            <div className="mc-engineBadge">Finalize by Cycle later</div>
          </div>
          {cycles.length ? (
            <div className="mc-liveRuleTable">
              <div className="mc-liveRuleTable__head mc-cycleRuleTable__grid">
                <div>Cycle</div><div>Description</div><div className="mc-liveRuleTable__headAction">Order</div><div className="mc-liveRuleTable__headAction">Status</div><div className="mc-liveRuleTable__headAction">Remove</div>
              </div>
              {cycles.map((cycle, cycleIndex) => {
                const cycleId = cycle.cycleId || cycle.id;
                return (
                  <div className="mc-liveRuleTable__row mc-cycleRuleTable__grid" key={cycleId}>
                    <div><input value={cycle.name || ""} onKeyDownCapture={(event) => event.stopPropagation()} onKeyUpCapture={(event) => event.stopPropagation()} onChange={(event) => setCycles((current) => current.map((item) => ((item.cycleId || item.id) === cycleId ? { ...item, name: event.target.value } : item)))} onBlur={(event) => updateCycle(cycleId, { name: event.target.value })} placeholder="Cycle name" /></div>
                    <div><input value={cycle.description || ""} onKeyDownCapture={(event) => event.stopPropagation()} onKeyUpCapture={(event) => event.stopPropagation()} onChange={(event) => setCycles((current) => current.map((item) => ((item.cycleId || item.id) === cycleId ? { ...item, description: event.target.value } : item)))} onBlur={(event) => updateCycle(cycleId, { description: event.target.value })} placeholder="Optional" /></div>
                    <div className="mc-liveRuleAction mc-cycleOrderActions">
                      <button type="button" className="mc-cycleOrderBtn" onClick={() => moveCycle(cycleId, -1)} disabled={cycleIndex === 0} aria-label={`Move ${cycle.name || "cycle"} up`} title="Move up">↑</button>
                      <button type="button" className="mc-cycleOrderBtn" onClick={() => moveCycle(cycleId, 1)} disabled={cycleIndex === cycles.length - 1} aria-label={`Move ${cycle.name || "cycle"} down`} title="Move down">↓</button>
                    </div>
                    <div className="mc-liveRuleAction">
                      <button type="button" className={`mc-configToggle ${cycle.active !== false ? "is-on" : ""}`} onClick={() => updateCycle(cycleId, { active: cycle.active === false })}>
                        <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span><strong>{cycle.active !== false ? "Active" : "Inactive"}</strong>
                      </button>
                    </div>
                    <div className="mc-liveRuleAction">
                      <button
                        type="button"
                        className="mc-engineBtn mc-siteRemovePill"
                        onClick={() => setDeleteCycleTarget(cycle)}
                        style={{ borderColor: "rgba(255, 83, 117, 0.45)", color: "#ff8fa3" }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mc-emptyState"><strong>No cycles created yet.</strong><p>Create Cycle 1 before preparing section counts. You can rename it to Morning, Late Shift, Closing, or anything the operation uses.</p></div>
          )}
        </article>
      </section>

      {deleteCycleTarget ? (
        <div
          className="mc-modalBackdrop"
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 80,
            display: "grid",
            placeItems: "center",
            padding: 24,
            background: "rgba(1, 12, 24, 0.58)",
            backdropFilter: "blur(16px)",
          }}
        >
          <div className="mc-confirmModal" role="dialog" aria-modal="true" style={{ width: "min(520px, 100%)", border: "1px solid rgba(103,232,249,.22)", borderRadius: 28, padding: 28, background: "linear-gradient(145deg, rgba(6,19,38,.96), rgba(8,32,58,.94))", boxShadow: "0 30px 90px rgba(0,0,0,.45)" }}>
            <h3 style={{ marginTop: 0 }}>Remove {deleteCycleTarget.name}?</h3>
            <p style={{ color: "#a7d8ff" }}>This removes the cycle from future selection. Existing sessions keep their saved cycle name.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 22 }}>
              <button className="mc-engineBtn" type="button" onClick={() => setDeleteCycleTarget(null)}>Cancel</button>
              <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={confirmRemoveCycle}>Remove Cycle</button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          className="mc-modalBackdrop"
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 80,
            display: "grid",
            placeItems: "center",
            padding: 24,
            background: "rgba(1, 12, 24, 0.58)",
            backdropFilter: "blur(16px)",
          }}
        >
          <div
            className="mc-confirmModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-section-title"
            style={{
              width: "min(520px, 100%)",
              border: "1px solid rgba(255, 83, 117, 0.34)",
              borderRadius: 28,
              padding: 28,
              background: "linear-gradient(145deg, rgba(7, 22, 42, 0.96), rgba(10, 35, 58, 0.92))",
              boxShadow: "0 28px 80px rgba(0, 0, 0, 0.38), 0 0 46px rgba(255, 83, 117, 0.16)",
              color: "#f7fbff",
            }}
          >
            <div className="mc-enginePage__eyebrow">Confirm remove section</div>
            <h3 id="remove-section-title">Remove {deleteTarget.name || "this section"}?</h3>
            <p>
              This removes the section from the ownership registry. Existing counts are not changed. Category/profile assignment will be added in the next phase.
            </p>
            <div className="mc-inlineActions" style={{ justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" className="mc-engineBtn" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                type="button"
                className="mc-engineBtn mc-engineBtn--primary"
                onClick={confirmRemoveSection}
                style={{ background: "linear-gradient(135deg, #ff5b7b, #ff8a3d)", boxShadow: "0 18px 40px rgba(255, 91, 123, 0.25)" }}
              >
                Remove Section
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <McPageFooter />
    </div>
  );
}
